import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NotificationsCtx = createContext(null);

const EMPTY = {
    news_unread: 0,
    convocations_pending: 0,
    communications_unread: 0,
    messages_unread: 0,
};

export function NotificationsProvider({ children }) {
    const { user } = useAuth();
    const [state, setState] = useState(EMPTY);
    const timer = useRef(null);

    const refresh = useCallback(async () => {
        if (!user) return;
        try {
            const { data } = await api.get("/notifications");
            setState({ ...EMPTY, ...data });
        } catch (e) {
            if (process.env.NODE_ENV !== "production") {
                console.warn("notifications refresh failed:", e);
            }
        }
    }, [user]);

    useEffect(() => {
        if (!user) {
            setState(EMPTY);
            return;
        }
        refresh();
        timer.current = setInterval(refresh, 45000);
        return () => timer.current && clearInterval(timer.current);
    }, [user, refresh]);

    const markNewsRead = useCallback(async () => {
        try {
            await api.post("/notifications/read-news");
            setState((s) => ({ ...s, news_unread: 0 }));
        } catch (e) {
            if (process.env.NODE_ENV !== "production") {
                console.warn("mark news read failed:", e);
            }
        }
    }, []);

    const markCommsRead = useCallback(async () => {
        try {
            await api.post("/notifications/read-communications");
            setState((s) => ({ ...s, communications_unread: 0 }));
        } catch (e) {
            if (process.env.NODE_ENV !== "production") {
                console.warn("mark comms read failed:", e);
            }
        }
    }, []);

    const value = useMemo(
        () => ({ ...state, refresh, markNewsRead, markCommsRead }),
        [state, refresh, markNewsRead, markCommsRead]
    );

    return (
        <NotificationsCtx.Provider value={value}>
            {children}
        </NotificationsCtx.Provider>
    );
}

export function useNotifications() {
    return useContext(NotificationsCtx) || {
        ...EMPTY,
        refresh: () => {},
        markNewsRead: () => {},
        markCommsRead: () => {},
    };
}
